import * as React from "react";
import {
  PageSection,
  Title,
  Button,
  Modal,
  ModalVariant,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Bullseye,
  EmptyState,
  EmptyStateBody,
  Gallery,
  Spinner,
} from "@patternfly/react-core";
import { useNavigate, useLocation } from "react-router-dom";
import "./Home.css";
import {
  buildTimeBranding,
  useSiteConfig,
  useSiteConfigState,
} from "../config/SiteConfigContext";
import { SiteCheck } from "../../config/bootstrap";
import { CheckCard } from "./CheckCard";
import { CheckInputForm, validateField } from "./CheckInputForm";
import { CheckWizard } from "./CheckWizard";
import { startCollection } from "./startCollection";

const NARROW_VIEWPORT = "(max-width: 768px)";
const introImageFile = (buildTimeBranding.introImageFile || "").trim();
const introImageSrc = introImageFile ? `/images/${introImageFile}` : "";

const Home: React.FunctionComponent = () => {
  const site = useSiteConfig();
  const { unavailable } = useSiteConfigState();
  const location = useLocation();
  const navigate = useNavigate();
  const [isNarrow, setIsNarrow] = React.useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(NARROW_VIEWPORT).matches
      : false,
  );
  const [activeCheck, setActiveCheck] = React.useState<SiteCheck | null>(null);
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [wizardStep, setWizardStep] = React.useState(1);
  const [signInOpen, setSignInOpen] = React.useState(false);
  const [inputs, setInputs] = React.useState<Record<string, string>>({});
  const [inputErrors, setInputErrors] = React.useState<
    Record<string, string | null>
  >({});
  const [redirecting, setRedirecting] = React.useState(false);
  const [redirectError, setRedirectError] = React.useState<string | null>(null);
  const redirectInFlight = React.useRef(false);

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia(NARROW_VIEWPORT);
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const checks = site?.checks || [];

  const openCheck = (check: SiteCheck) => {
    if (redirectInFlight.current) {
      return;
    }
    setRedirectError(null);
    setActiveCheck(check);
    setInputs({});
    setInputErrors({});
    // PatternFly hides #root with aria-hidden while this control still has focus.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (check.authMode === "app-consent") {
      const param = check.wizardQueryParam || "step";
      setWizardStep(1);
      navigate(`${location.pathname}?${param}=1`, { replace: true });
      setWizardOpen(true);
      return;
    }
    setSignInOpen(true);
  };

  React.useEffect(() => {
    if (!site) {
      return;
    }
    const params = new URLSearchParams(location.search);
    for (const check of site.checks) {
      if (check.authMode !== "app-consent" || !check.wizardQueryParam) {
        continue;
      }
      const stepParam = params.get(check.wizardQueryParam);
      if (!stepParam) {
        continue;
      }
      const stepNumber = parseInt(stepParam, 10);
      const max = check.wizardSteps?.length || 5;
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= max) {
        setActiveCheck(check);
        setWizardStep(stepNumber);
        setWizardOpen(true);
        break;
      }
    }
  }, [location, site]);

  const handleDelegatedContinue = async () => {
    if (!activeCheck || redirectInFlight.current) {
      return;
    }
    const schema = activeCheck.inputSchema || [];
    const nextErrors: Record<string, string | null> = {};
    let invalid = false;
    schema.forEach((field) => {
      const error = validateField(field, inputs[field.name] || "");
      nextErrors[field.name] = error;
      if (error) {
        invalid = true;
      }
    });
    setInputErrors(nextErrors);
    if (invalid) {
      setSignInOpen(true);
      return;
    }
    redirectInFlight.current = true;
    setRedirectError(null);
    setRedirecting(true);
    setSignInOpen(false);
    try {
      const url = await startCollection(activeCheck.taskDefId, "run", inputs);
      window.location.href = url;
    } catch (error) {
      redirectInFlight.current = false;
      setRedirecting(false);
      const message = error instanceof Error ? error.message.trim() : "";
      setRedirectError(
        message || "The sign-in page could not be prepared. Try again.",
      );
    }
  };

  const closeWizard = () => {
    setWizardOpen(false);
    navigate(location.pathname, { replace: true });
  };

  if (unavailable || !site || checks.length === 0) {
    return (
      <PageSection hasBodyWrapper={false}>
        <Alert variant="info" title="No services currently available" isInline>
          <p>There are no services currently available for this site.</p>
        </Alert>
      </PageSection>
    );
  }

  return (
    <PageSection hasBodyWrapper={false}>
      {redirecting && (
        <Bullseye>
          <EmptyState
            titleText="Redirecting to Microsoft"
            headingLevel="h4"
            icon={Spinner}
            aria-live="polite"
          >
            <EmptyStateBody>Preparing the sign-in page.</EmptyStateBody>
          </EmptyState>
        </Bullseye>
      )}
      {!redirecting && (
        <>
          {redirectError && (
            <Alert variant="danger" title="Unable to start the check" isInline>
              {redirectError}
            </Alert>
          )}
          <Alert
            variant="info"
            title="Security is Our Priority"
            isInline
            isExpandable={isNarrow}
            toggleAriaLabel="Toggle security priority details"
          >
            <p>
              We maintain strict security controls to protect your data, meeting
              or exceeding industry standards. All data collected is protected
              by AES-256 encryption and stored in a logically isolated, highly
              restricted environment and destroyed within 30 days of reporting
              completion. Administrative access to our systems is secured by VPN
              and MFA on all operations with detailed audit logging alongside
              other baseline controls.
            </p>
          </Alert>
          <div className="home-intro-layout">
            <div className="home-intro-text">
              <Title headingLevel="h1" size="xl">
                {site.productName} Services
              </Title>
              <br />
              <p style={{ margin: 0 }}>
                As organisations adopt AI, understanding which users, agents and
                machine identities have access to your data and resources is
                more important than ever before.
                <br />
                {site.productName} provides an easy, fast way for you to assess
                the security of your environment(s), identify hidden risks and
                provide you with clear recommendations so that you can take
                action.
                <br />
                <br />
                Select one of the {site.productName} options below to perform an
                assessment.
              </p>
            </div>
            {introImageSrc ? (
              <img
                className="home-intro-image"
                src={introImageSrc}
                alt={`${site.productName} banner`}
              />
            ) : null}
          </div>
          <Gallery hasGutter>
            {checks.map((check) => (
              <CheckCard
                key={check.taskDefId}
                check={check}
                onStart={openCheck}
              />
            ))}
          </Gallery>

          <Modal
            isOpen={signInOpen}
            variant={ModalVariant.small}
            aria-label="Sign-in required for check notice"
            onClose={() => setSignInOpen(false)}
          >
            <ModalHeader
              title={`Sign-in required for ${activeCheck?.productLabel} status`}
            />
            <ModalBody>
              <p>
                To view the {activeCheck?.productLabel} check status page, you
                must be signed in with your authorised account.
              </p>
              {activeCheck?.inputSchema &&
                activeCheck.inputSchema.length > 0 && (
                  <CheckInputForm
                    schema={activeCheck.inputSchema}
                    values={inputs}
                    errors={inputErrors}
                    onChange={(name, value) => {
                      setInputs((prev) => ({ ...prev, [name]: value }));
                      if (inputErrors[name]) {
                        const field = activeCheck.inputSchema?.find(
                          (item) => item.name === name,
                        );
                        setInputErrors((prev) => ({
                          ...prev,
                          [name]: field ? validateField(field, value) : null,
                        }));
                      }
                    }}
                    onBlur={(name, value) => {
                      const field = activeCheck.inputSchema?.find(
                        (item) => item.name === name,
                      );
                      setInputErrors((prev) => ({
                        ...prev,
                        [name]: field ? validateField(field, value) : null,
                      }));
                    }}
                  />
                )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="primary"
                onClick={() => void handleDelegatedContinue()}
                isDisabled={Boolean(
                  activeCheck?.inputSchema?.some((field) =>
                    validateField(field, inputs[field.name] || ""),
                  ),
                )}
              >
                Continue
              </Button>
              <Button variant="link" onClick={() => setSignInOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          {activeCheck?.authMode === "app-consent" && (
            <CheckWizard
              check={activeCheck}
              site={site}
              isOpen={wizardOpen}
              startIndex={wizardStep}
              onClose={closeWizard}
              onStepChange={(step) => {
                setWizardStep(step);
                const param = activeCheck.wizardQueryParam || "step";
                navigate(`${location.pathname}?${param}=${step}`, {
                  replace: true,
                });
              }}
            />
          )}
        </>
      )}
    </PageSection>
  );
};

export { Home };
